import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Grid,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Collapse
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PeopleIcon from '@mui/icons-material/People';
import { useApi } from '../contexts/ApiContext';
import { usePermissions } from '../hooks/usePermissions';

const POLL_INTERVAL_MS = 2000;
const STORAGE_KEY_VIEW = 'skillsMonitor_viewMode';
const STORAGE_KEY_CAMPAIGN = 'skillsMonitor_campaignFilter';

const formatCountChip = (value, sx = {}) => (
  <Chip
    size="small"
    variant="outlined"
    color="default"
    label={Number(value || 0).toLocaleString()}
    sx={{ minWidth: 44, ...sx }}
  />
);

const formatSeconds = (value) => {
  const total = Math.max(0, Number(value) || 0);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  const paddedSeconds = String(seconds).padStart(2, '0');
  return `${minutes}:${paddedSeconds}`;
};

const trendConfig = {
  membersCount: 'good',
  availableCount: 'bad',
  inUseCount: 'good',
  pausedCount: 'bad',
  callsTotal: 'good',
  callsWaiting: 'bad'
};

const getTrendColor = (delta, mode) => {
  if (delta === 0 || !mode) return 'text.disabled';
  const isUp = delta > 0;
  if (mode === 'neutral') return 'text.disabled';
  if (mode === 'good') return isUp ? 'success.main' : 'error.main';
  return isUp ? 'error.main' : 'success.main';
};

const renderTrend = (delta, mode) => {
  if (!Number.isFinite(delta)) return null;
  const Icon = delta === 0 ? TrendingFlatIcon : delta > 0 ? TrendingUpIcon : TrendingDownIcon;
  return <Icon fontSize="inherit" sx={{ color: getTrendColor(delta, mode) }} />;
};

const renderTrendChevron = (delta, mode) => {
  if (!Number.isFinite(delta)) return null;
  if (delta === 0) return <ChevronRightIcon fontSize="inherit" sx={{ color: 'text.disabled' }} />;
  const Icon = delta > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  return <Icon fontSize="inherit" sx={{ color: getTrendColor(delta, mode) }} />;
};

const renderTrendTriangle = (delta, mode) => {
  if (!Number.isFinite(delta)) return null;
  if (delta === 0) return <ChevronRightIcon fontSize="inherit" sx={{ color: 'text.disabled' }} />;
  const Icon = delta > 0 ? ArrowUpwardIcon : ArrowDownwardIcon;
  return <Icon fontSize="inherit" sx={{ color: getTrendColor(delta, mode) }} />;
};

/* ────────────── Card View Component ────────────── */

function SkillCard({ queue, skillTypeModes, skillStatuses, statusLoading, engineCallsInProgress, showLevels, previousQueuesRef, toggleSkillStatus, handleOpenReset, canAction }) {
  const connected = queue.membersCount ?? (queue.agentsConnected || []).length;
  const available = queue.availableCount ?? (queue.agentsAvailable || []).length;
  const inUse = queue.inUseCount ?? 0;
  const paused = queue.pausedCount ?? 0;
  const total = connected || 1;
  const isRunning = !!skillStatuses[queue.name];
  const cip = engineCallsInProgress[queue.name] || 0;

  return (
    <Card
      sx={{
        height: '100%',
        borderLeft: 4,
        borderColor: isRunning ? 'success.main' : 'grey.400',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 6 }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {queue.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {skillTypeModes[queue.name] || '-'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25}>
            {canAction('control_skill_monitor') && (
              <Tooltip title={isRunning ? 'Detener' : 'Iniciar'}>
                <span>
                  <IconButton
                    size="small"
                    color={isRunning ? 'error' : 'success'}
                    onClick={() => toggleSkillStatus(queue.name)}
                    disabled={statusLoading[queue.name]}
                  >
                    {isRunning ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canAction('control_skill_monitor') && (
              <Tooltip title="Reset">
                <span>
                  <IconButton size="small" color="error" onClick={() => handleOpenReset(queue.name)}>
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Agents Section */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <PeopleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">AGENTES</Typography>
          </Stack>
          <Grid container spacing={1}>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', bgcolor: theme => alpha(theme.palette.info.main, 0.08), borderRadius: 1, py: 0.5 }}>
                <Typography variant="h6" fontSize="1.1rem" fontWeight={700}>{connected}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>En linea</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', bgcolor: theme => alpha(theme.palette.success.main, 0.08), borderRadius: 1, py: 0.5 }}>
                <Typography variant="h6" fontSize="1.1rem" fontWeight={700} color="success.main">{available}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>Dispo</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', bgcolor: theme => alpha(theme.palette.error.main, 0.08), borderRadius: 1, py: 0.5 }}>
                <Typography variant="h6" fontSize="1.1rem" fontWeight={700} color="error.main">{inUse}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>Llamada</Typography>
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box sx={{ textAlign: 'center', bgcolor: theme => alpha(theme.palette.warning.main, 0.08), borderRadius: 1, py: 0.5 }}>
                <Typography variant="h6" fontSize="1.1rem" fontWeight={700} color="warning.main">{paused}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>Pausa</Typography>
              </Box>
            </Grid>
          </Grid>
          {connected > 0 && (
            <Stack direction="row" spacing={0} sx={{ mt: 0.75, height: 6, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
              {available > 0 && <Box sx={{ width: `${(available / total) * 100}%`, bgcolor: 'success.main' }} />}
              {inUse > 0 && <Box sx={{ width: `${(inUse / total) * 100}%`, bgcolor: 'error.main' }} />}
              {paused > 0 && <Box sx={{ width: `${(paused / total) * 100}%`, bgcolor: 'warning.main' }} />}
            </Stack>
          )}
        </Box>

        {/* Calls Section */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
            <PhoneInTalkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">LLAMADAS</Typography>
          </Stack>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Marcando</Typography>
              <Typography variant="subtitle2" fontWeight={700}>{cip}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">En curso</Typography>
              <Typography variant="subtitle2" fontWeight={700}>{queue.callsTotal ?? 0}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">En espera</Typography>
              <Typography variant="subtitle2" fontWeight={700} color={queue.callsWaiting > 0 ? 'error.main' : 'text.primary'}>
                {queue.callsWaiting ?? 0}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Finalizadas</Typography>
              <Typography variant="subtitle2">{Number(queue.completedCalls ?? 0).toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">Abandonadas</Typography>
              <Typography variant="subtitle2" color={(queue.abandonedCalls ?? 0) > 0 ? 'error.main' : 'text.primary'}>
                {Number(queue.abandonedCalls ?? 0).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Levels */}
        {showLevels && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">T. Espera</Typography>
                <Typography variant="subtitle2">{formatSeconds(queue.avgHoldTime ?? 0)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">TMO</Typography>
                <Typography variant="subtitle2">{formatSeconds(queue.avgTalkTime ?? 0)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">SL</Typography>
                <Typography variant="subtitle2">{formatSeconds(queue.serviceLevelSeconds ?? 0)}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">%SL</Typography>
                <Typography variant="subtitle2">{Number(queue.serviceLevelPerf ?? 0).toFixed(1)}%</Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ────────────── Main Page ────────────── */

export default function SkillsMonitorPage() {
  const { dialer, skills: skillsApi, campaigns: campaignsApi } = useApi();
  const { canAction } = usePermissions();
  const [queues, setQueues] = useState([]);
  const [skillTypeModes, setSkillTypeModes] = useState({});
  const [skillDialingModes, setSkillDialingModes] = useState({});
  const [skillCampaigns, setSkillCampaigns] = useState({});
  const [skillStatuses, setSkillStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollingRef = useRef(false);
  const [resetDialog, setResetDialog] = useState({ open: false, queue: null });
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [showLevels, setShowLevels] = useState(false);
  const [engineCallsInProgress, setEngineCallsInProgress] = useState({});
  const previousQueuesRef = useRef({});
  const currentQueuesRef = useRef({});
  const [collapsedCampaigns, setCollapsedCampaigns] = useState({});

  // Persistent preferences
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'table';
    return localStorage.getItem(STORAGE_KEY_VIEW) || 'table';
  });
  const [campaignFilter, setCampaignFilter] = useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(STORAGE_KEY_CAMPAIGN) || '';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY_VIEW, viewMode);
  }, [viewMode]);
  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY_CAMPAIGN, campaignFilter || '');
  }, [campaignFilter]);

  const loadQueues = useCallback(async (options = {}) => {
    if (!dialer?.getAsteriskQueues) return;
    const { silent = false } = options;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const response = await dialer.getAsteriskQueues();
      const data = response?.data?.queues || response?.queues || [];
      const nextMap = {};
      data.forEach((item) => {
        if (item?.name) nextMap[item.name] = item;
      });
      previousQueuesRef.current = currentQueuesRef.current;
      currentQueuesRef.current = nextMap;
      setQueues(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) setError(err?.message || 'No se pudo cargar el monitor de skills');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dialer]);

  useEffect(() => { loadQueues(); }, [loadQueues]);

  useEffect(() => {
    const loadSkillsMeta = async () => {
      if (!skillsApi?.getAll) return;
      try {
        const response = await skillsApi.getAll();
        const data = response?.data?.skills || response?.data || response?.skills || response || [];
        const typeMap = {};
        const modeMap = {};
        const campMap = {};
        (Array.isArray(data) ? data : []).forEach((skill) => {
          const def = skill.skilldef || skill.skillDef || skill.name;
          if (!def) return;
          const typeLabel = skill.dialing_mode || skill.skilltype || skill.skill_type || skill.type || '';
          const modeLabel = skill.overdialing_mode || '';
          if (skill.dialing_mode) {
            modeMap[def] = String(skill.dialing_mode || '').toLowerCase();
          } else if (typeLabel) {
            modeMap[def] = String(typeLabel || '').toLowerCase();
          }
          const normalizedType = (typeLabel || '').toLowerCase();
          if (normalizedType === 'manual' || normalizedType === 'preview') {
            typeMap[def] = '';
          } else if (typeLabel && modeLabel) {
            typeMap[def] = `${typeLabel} - ${modeLabel}`;
          } else {
            typeMap[def] = typeLabel || modeLabel || '';
          }
          campMap[def] = {
            id: skill.idcampaign ?? '',
            name: skill.campaign_name || ''
          };
        });
        setSkillTypeModes(typeMap);
        setSkillDialingModes(modeMap);
        setSkillCampaigns(campMap);
      } catch (err) {
        setSkillTypeModes({});
        setSkillDialingModes({});
      }
    };

    const loadStatuses = async () => {
      if (!dialer?.getEngines) return;
      try {
        const response = await dialer.getEngines();
        const list = response?.data ?? response ?? [];
        const map = {};
        const cipMap = {};
        list.forEach((engine) => {
          const def = engine?.skillDef || engine?.skilldef;
          if (!def) return;
          map[def] = !!engine.isRunning;
          cipMap[def] = engine?.statistics?.callsInProgress ?? 0;
        });
        setSkillStatuses(map);
        setEngineCallsInProgress(cipMap);
      } catch (err) {
        setSkillStatuses({});
      }
    };

    loadSkillsMeta();
    loadStatuses();
  }, [skillsApi, dialer]);

  const loadEngineStats = useCallback(async () => {
    if (!dialer?.getEngines) return;
    try {
      const response = await dialer.getEngines();
      const list = response?.data ?? response ?? [];
      const cipMap = {};
      const statusMap = {};
      list.forEach((engine) => {
        const def = engine?.skillDef || engine?.skilldef;
        if (!def) return;
        statusMap[def] = !!engine.isRunning;
        cipMap[def] = engine?.statistics?.callsInProgress ?? 0;
      });
      setEngineCallsInProgress(cipMap);
      setSkillStatuses(statusMap);
    } catch (err) {
      // ignore
    }
  }, [dialer]);

  useEffect(() => {
    let isMounted = true;
    const tick = async () => {
      if (!isMounted || pollingRef.current) return;
      pollingRef.current = true;
      try {
        await Promise.all([loadQueues({ silent: true }), loadEngineStats()]);
      } finally {
        pollingRef.current = false;
      }
    };
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      tick();
    }, POLL_INTERVAL_MS);
    return () => { isMounted = false; clearInterval(intervalId); };
  }, [loadQueues, loadEngineStats]);

  // ──── Derived data ────

  const rows = useMemo(() => {
    return [...queues]
      .filter((queue) => {
        const mode = (skillDialingModes[queue.name] || '').toLowerCase();
        return mode !== 'manual' && mode !== 'preview';
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [queues, skillDialingModes]);

  const campaignOptions = useMemo(() => {
    const seen = new Map();
    Object.values(skillCampaigns).forEach(({ id, name }) => {
      if (id && !seen.has(String(id))) seen.set(String(id), name || `Campaña ${id}`);
    });
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [skillCampaigns]);

  const filteredRows = useMemo(() => {
    if (!campaignFilter) return rows;
    return rows.filter(q => String(skillCampaigns[q.name]?.id || '') === String(campaignFilter));
  }, [rows, campaignFilter, skillCampaigns]);

  const groupedRows = useMemo(() => {
    const groups = new Map();
    filteredRows.forEach(q => {
      const name = skillCampaigns[q.name]?.name || 'Sin campaña';
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(q);
    });
    return groups;
  }, [filteredRows, skillCampaigns]);

  // ──── Handlers ────

  const renderMetric = (value, prevValue, mode, formatter, showTrend = true) => {
    const delta = Number.isFinite(prevValue) ? (Number(value) || 0) - (Number(prevValue) || 0) : null;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
        <Typography variant="subtitle2">
          {formatter ? formatter(value) : Number(value || 0).toLocaleString()}
        </Typography>
        {!showTrend || delta === null ? null : (
          <Box sx={{ fontSize: 16, display: 'inline-flex', alignItems: 'center' }}>
            {renderTrend(delta, mode)}
          </Box>
        )}
      </Stack>
    );
  };

  const handleOpenReset = (queue = null) => {
    setResetError('');
    setResetDialog({ open: true, queue });
  };
  const handleCloseReset = () => {
    if (resetLoading) return;
    setResetDialog({ open: false, queue: null });
  };
  const handleConfirmReset = async () => {
    if (!dialer?.resetAsteriskQueues) return;
    setResetLoading(true);
    setResetError('');
    try {
      await dialer.resetAsteriskQueues(resetDialog.queue || null);
      await loadQueues({ silent: true });
      setResetDialog({ open: false, queue: null });
    } catch (err) {
      setResetError(err?.message || 'No se pudo resetear la cola');
    } finally {
      setResetLoading(false);
    }
  };
  const toggleSkillStatus = async (skillDef) => {
    if (!dialer?.startSkill || !dialer?.stopSkill) return;
    const isRunning = !!skillStatuses[skillDef];
    const action = isRunning ? 'stopSkill' : 'startSkill';
    try {
      setStatusLoading(prev => ({ ...prev, [skillDef]: true }));
      await dialer[action](skillDef);
      setSkillStatuses(prev => ({ ...prev, [skillDef]: !isRunning }));
    } catch (err) {
      // ignore
    } finally {
      setStatusLoading(prev => ({ ...prev, [skillDef]: false }));
    }
  };

  // ──── Column count for grouped header ────
  const totalCols = 12 + (showLevels ? 4 : 0);

  // ──── Render table row ────
  const renderTableRow = (queue) => {
    const connected = queue.agentsConnected || [];
    const available = queue.agentsAvailable || [];
    const callsTotal = queue.callsTotal ?? 0;
    const callsWaiting = queue.callsWaiting ?? 0;
    const inUseCount = queue.inUseCount ?? 0;
    const pausedCount = queue.pausedCount ?? 0;
    const completedCalls = queue.completedCalls ?? 0;
    const abandonedCalls = queue.abandonedCalls ?? 0;
    const prevRow = previousQueuesRef.current[queue.name] || {};
    const prevMembersCount = prevRow.membersCount ?? (prevRow.agentsConnected ? prevRow.agentsConnected.length : null);
    const prevAvailableCount = prevRow.availableCount ?? (prevRow.agentsAvailable ? prevRow.agentsAvailable.length : 0);
    const prevInUseCount = prevRow.inUseCount ?? null;
    const prevPausedCount = prevRow.pausedCount ?? null;
    const prevCallsTotal = prevRow.callsTotal ?? null;
    const prevCallsWaiting = prevRow.callsWaiting ?? null;

    return (
      <TableRow key={queue.name}>
        <TableCell sx={{ px: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle2" noWrap>{queue.name || 'Sin nombre'}</Typography>
        </TableCell>
        <TableCell sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color="text.secondary" noWrap>{skillTypeModes[queue.name] || '-'}</Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {formatCountChip(queue.membersCount || connected.length, {
              bgcolor: alpha(
                prevMembersCount == null ? '#ffffff'
                  : (queue.membersCount || connected.length) > prevMembersCount ? '#16a34a'
                    : (queue.membersCount || connected.length) < prevMembersCount ? '#ef4444' : '#ffffff',
                prevMembersCount == null ? 0 : 0.12
              ),
              borderColor: '#e5e7eb'
            })}
          </Stack>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {formatCountChip(queue.availableCount || available.length, {
              bgcolor: alpha(
                prevAvailableCount == null ? '#ffffff'
                  : (queue.availableCount || available.length) > prevAvailableCount ? '#ef4444'
                    : (queue.availableCount || available.length) < prevAvailableCount ? '#16a34a' : '#ffffff',
                prevAvailableCount == null ? 0 : 0.12
              ),
              borderColor: '#e5e7eb'
            })}
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 96 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <Typography variant="subtitle2">{Number(inUseCount).toLocaleString()}</Typography>
            {Number.isFinite(prevInUseCount) && (
              <Box sx={{ fontSize: 14 }}>{renderTrendChevron(inUseCount - prevInUseCount, trendConfig.inUseCount)}</Box>
            )}
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
            <Typography variant="subtitle2">{Number(pausedCount).toLocaleString()}</Typography>
            {Number.isFinite(prevPausedCount) && (
              <Box sx={{ fontSize: 12 }}>{renderTrendTriangle(pausedCount - prevPausedCount, trendConfig.pausedCount)}</Box>
            )}
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle2">{Number(engineCallsInProgress[queue.name] || 0).toLocaleString()}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {renderMetric(callsTotal, prevCallsTotal, trendConfig.callsTotal)}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {renderMetric(callsWaiting, prevCallsWaiting, trendConfig.callsWaiting)}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle2">{Number(completedCalls).toLocaleString()}</Typography>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="subtitle2">{Number(abandonedCalls).toLocaleString()}</Typography>
        </TableCell>
        {showLevels && (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="subtitle2">{formatSeconds(queue.avgHoldTime ?? 0)}</Typography>
          </TableCell>
        )}
        {showLevels && (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="subtitle2">{formatSeconds(queue.avgTalkTime ?? 0)}</Typography>
          </TableCell>
        )}
        {showLevels && (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="subtitle2">{formatSeconds(queue.serviceLevelSeconds ?? 0)}</Typography>
          </TableCell>
        )}
        {showLevels && (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            <Typography variant="subtitle2">{Number(queue.serviceLevelPerf ?? 0).toFixed(1)}%</Typography>
          </TableCell>
        )}
        <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
            {canAction('control_skill_monitor') && (
              <Tooltip title={skillStatuses[queue.name] ? 'Detener skill' : 'Iniciar skill'}>
                <span>
                  <IconButton
                    size="small"
                    color={skillStatuses[queue.name] ? 'error' : 'success'}
                    onClick={() => toggleSkillStatus(queue.name)}
                    disabled={statusLoading[queue.name]}
                  >
                    {skillStatuses[queue.name] ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canAction('control_skill_monitor') && (
              <Tooltip title="Reset metricas">
                <span>
                  <IconButton size="small" color="error" onClick={() => handleOpenReset(queue.name)}>
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box>
      {/* ──── Header Card ──── */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontSize="1rem">Monitor de Skills</Typography>
              <Typography variant="caption" color="text.secondary">
                Actualizado {lastUpdated ? lastUpdated.toLocaleTimeString() : '\u2014'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {/* Campaign filter */}
              <TextField
                select
                size="small"
                label="Campaña"
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">Todas</MenuItem>
                {campaignOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>

              {/* View toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, v) => v && setViewMode(v)}
                size="small"
              >
                <ToggleButton value="table">
                  <Tooltip title="Vista tabla"><ViewListIcon fontSize="small" /></Tooltip>
                </ToggleButton>
                <ToggleButton value="cards">
                  <Tooltip title="Vista cards"><GridViewIcon fontSize="small" /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <FormControlLabel
                control={<Switch checked={showLevels} onChange={(e) => setShowLevels(e.target.checked)} size="small" />}
                label="Niveles"
                sx={{ mr: 0 }}
              />

              {canAction('control_skill_monitor') && (
                <Button variant="outlined" color="error" onClick={() => handleOpenReset(null)} disabled={loading} size="small">
                  Reset todas
                </Button>
              )}
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => loadQueues()} disabled={loading} size="small">
                Actualizar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ──── Content ──── */}
      {loading ? (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={22} />
          <Typography>Cargando colas...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredRows.length === 0 ? (
        <Alert severity="info">No hay colas disponibles{campaignFilter ? ' para esta campaña' : ''} en este momento.</Alert>
      ) : viewMode === 'cards' ? (
        /* ──── Cards View ──── */
        Array.from(groupedRows.entries()).map(([campName, campRows]) => {
          const isCollapsed = !!collapsedCampaigns[campName];
          return (
            <Box key={campName} sx={{ mb: 3 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setCollapsedCampaigns(prev => ({ ...prev, [campName]: !prev[campName] }))}
              >
                <IconButton size="small" sx={{ p: 0 }}>
                  {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
                <Typography variant="subtitle1" fontWeight={700}>{campName}</Typography>
                <Chip label={campRows.length} size="small" />
              </Stack>
              <Collapse in={!isCollapsed}>
                <Grid container spacing={2}>
                  {campRows.map(queue => (
                    <Grid item xs={12} md={6} lg={4} key={queue.name}>
                      <SkillCard
                        queue={queue}
                        skillTypeModes={skillTypeModes}
                        skillStatuses={skillStatuses}
                        statusLoading={statusLoading}
                        engineCallsInProgress={engineCallsInProgress}
                        showLevels={showLevels}
                        previousQueuesRef={previousQueuesRef}
                        toggleSkillStatus={toggleSkillStatus}
                        handleOpenReset={handleOpenReset}
                        canAction={canAction}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Box>
          );
        })
      ) : (
        /* ──── Table View ──── */
        <Card>
          <CardContent sx={{ p: { xs: 1, md: 2 } }}>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  '& thead tr:first-of-type th': {
                    borderBottom: theme => `2px solid ${theme.palette.divider}`
                  },
                  '& thead tr:nth-of-type(2) th:nth-of-type(1)': {
                    borderLeft: theme => `2px solid ${theme.palette.divider}`
                  },
                  '& thead tr:nth-of-type(2) th:nth-of-type(5)': {
                    borderLeft: theme => `2px solid ${theme.palette.divider}`
                  },
                  '& thead tr:nth-of-type(2) th:nth-of-type(10)': {
                    borderLeft: theme => `2px solid ${theme.palette.divider}`
                  },
                  '& tbody td:nth-of-type(3)': {
                    borderLeft: theme => `2px solid ${theme.palette.divider}`
                  },
                  '& tbody td:nth-of-type(7)': {
                    borderLeft: theme => `2px solid ${theme.palette.divider}`
                  },
                  ...(showLevels && {
                    '& tbody td:nth-of-type(12)': {
                      borderLeft: theme => `2px solid ${theme.palette.divider}`
                    }
                  })
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ width: showLevels ? '12%' : '14%', bgcolor: 'action.hover', fontWeight: 600 }}>
                      Skill
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ width: showLevels ? '10%' : '12%', bgcolor: 'action.hover', fontWeight: 600 }}>
                      Tipo / Modo
                    </TableCell>
                    <TableCell colSpan={4} align="center" sx={{ bgcolor: theme => alpha(theme.palette.info.main, 0.12), fontWeight: 700 }}>
                      Agentes
                    </TableCell>
                    <TableCell colSpan={5} align="center" sx={{ bgcolor: theme => alpha(theme.palette.success.main, 0.12), fontWeight: 700 }}>
                      Llamadas
                    </TableCell>
                    {showLevels && (
                      <TableCell colSpan={4} align="center" sx={{ bgcolor: theme => alpha(theme.palette.warning.main, 0.12), fontWeight: 700 }}>
                        Niveles
                      </TableCell>
                    )}
                    <TableCell rowSpan={2} align="center" sx={{ width: showLevels ? '10%' : '12%', bgcolor: 'action.hover', fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper' }}><Tooltip title="Agentes en linea"><span>En linea</span></Tooltip></TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}><Tooltip title="Agentes disponibles"><span>Dispo</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Agentes en llamada"><span>En llamada</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Agentes en pausa"><span>En pausa</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Llamadas marcandose por el dialer"><span>Marcando</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Llamadas en curso"><span>En curso</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Llamadas en espera"><span>En espera</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Llamadas finalizadas"><span>Finaliz...</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Llamadas abandonadas"><span>Aband...</span></Tooltip></TableCell>
                    {showLevels && <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Tiempo promedio de espera"><span>T. Espera</span></Tooltip></TableCell>}
                    {showLevels && <TableCell align="right" sx={{ bgcolor: 'background.paper' }}>TMO</TableCell>}
                    {showLevels && <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="Service Level en segundos"><span>SL</span></Tooltip></TableCell>}
                    {showLevels && <TableCell align="right" sx={{ bgcolor: 'background.paper' }}><Tooltip title="% de llamadas contestadas dentro del SL"><span>%SL</span></Tooltip></TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from(groupedRows.entries()).map(([campName, campRows]) => {
                    const isCollapsed = !!collapsedCampaigns[campName];
                    return (
                      <React.Fragment key={campName}>
                        {/* Campaign group header */}
                        <TableRow
                          sx={{ cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => setCollapsedCampaigns(prev => ({ ...prev, [campName]: !prev[campName] }))}
                        >
                          <TableCell
                            colSpan={totalCols}
                            sx={{
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
                              py: 0.75,
                              borderBottom: theme => `1px solid ${theme.palette.divider}`
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              {isCollapsed ? <ExpandMoreIcon fontSize="small" color="primary" /> : <ExpandLessIcon fontSize="small" color="primary" />}
                              <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                                {campName}
                              </Typography>
                              <Chip label={`${campRows.length} skill${campRows.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {!isCollapsed && campRows.map(renderTableRow)}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ──── Reset Dialog ──── */}
      <Dialog open={resetDialog.open} onClose={handleCloseReset}>
        <DialogTitle>Resetear metricas</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {resetDialog.queue
              ? `Se reiniciaran las metricas de la cola ${resetDialog.queue}.`
              : 'Se reiniciaran las metricas de todas las colas.'}
          </Typography>
          {resetError && <Alert severity="error">{resetError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReset} disabled={resetLoading}>Cancelar</Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained" disabled={resetLoading}>
            {resetLoading ? 'Reseteando...' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
